import { findDecisionsBlock, findFencedBlock, parseQuestionOptions, recommendedOption, type FactoryQuestionOption, type FactoryQuestions } from './factory-questions';
import { indentFollowingLines, readFollowingLines } from './list-item-lines';

/**
 * T-55: further changes the software factory's AI builder suggests on a task mirrored from jflo. The
 * factory posts a comment holding one fenced block, the one its coding agent writes
 * (software-factory's shared/factory-suggestions.ts, kept in step with it):
 *
 * ```factory-suggestions
 * { "suggestions": [{ "id": "retry", "title": "Retry the sync on a timeout", "detail": "…" }] }
 * ```
 *
 * The task shows each suggestion with what can be decided about it from jflo (Yes please, New branch,
 * Backlog, Skip). People pick one per suggestion and apply them together, as one comment in the
 * factory's decisions format (formatSuggestionDecisions), which the card reads back (parseSuggestionDecisions).
 *
 * T-99 (the factory's SF-T-308): a factory-questions block in the wrap phase is a round of suggestions too
 * (findFactorySuggestions reads it as one), and an item can offer options: picking one is Yes please, with
 * the option(s) and any steer in the decision's note. Skip is what No thanks was (the action is still `no`;
 * older messages say No thanks, read as Skip).
 */

export const FACTORY_SUGGESTIONS_LANG = 'factory-suggestions';

/** Most suggestions in one block, and characters per piece of text. */
export const FACTORY_SUGGESTIONS_MAX = 10;
const TITLE_MAX = 200;
const DETAIL_MAX = 2000;
/** Characters in what building a suggestion would change, or leaving it out would mean. */
const NOTE_MAX = 500;
/** Characters in a decision's note: a reason, or the options picked and a steer (room for eight 200-character options). */
export const SUGGESTION_NOTE_MAX = 2500;

export interface FactorySuggestion {
  id: string;
  title: string;
  /** Why, and what it would involve. */
  detail?: string;
  /** T-99: what building it would change, and how big it is. */
  change?: string;
  /** T-99: what stays as it is without it, and the risk. */
  without?: string;
  /** T-99: the action the agent recommends. */
  recommend?: SuggestionAction;
  /** T-99: ways to do it, to pick from (none: Yes please alone). */
  options?: FactoryQuestionOption[];
  /** T-99: several options can be picked together. */
  multiSelect?: boolean;
  /** T-99: the label of the option the agent recommends. */
  recommended?: string;
}

export interface FactorySuggestions {
  suggestions: FactorySuggestion[];
}

/**
 * What can be decided about a suggestion from jflo:
 * - yes: the AI builder builds it on this task's branch and PR (the note: the options picked and a steer).
 * - branch: a new task stacked on this task's branch, with its own PR (T-99).
 * - backlog: a new task for later.
 * - no: Skip (No thanks before T-99): we decided not to; the note says why (optional).
 */
export const SUGGESTION_ACTIONS = ['yes', 'branch', 'backlog', 'no'] as const;
export type SuggestionAction = (typeof SUGGESTION_ACTIONS)[number];

export const SUGGESTION_ACTION_LABELS: Record<SuggestionAction, string> = {
  yes: 'Yes please',
  branch: 'New branch',
  backlog: 'Backlog',
  no: 'Skip',
};

/** T-99: what Skip was called before; older decisions messages say it, read as Skip. */
const OLD_NO_LABEL = 'No thanks';

/** T-99: the action a suggestion recommends, with the spellings an agent might use; null for anything else. */
function recommendedAction(value: unknown): SuggestionAction | null {
  const v = typeof value === 'string' ? value.trim().toLowerCase().replace(/[_-]+/g, ' ') : '';
  if (v === 'yes' || v === 'yes please') return 'yes';
  if (v === 'branch' || v === 'new branch') return 'branch';
  if (v === 'backlog') return 'backlog';
  if (v === 'no' || v === 'no thanks' || v === 'skip') return 'no';
  return null;
}

const text = (v: unknown, max: number): string => (typeof v === 'string' ? v.trim().slice(0, max) : '');

/** The block's JSON, checked and trimmed; null when it isn't usable. */
export function parseFactorySuggestionsJson(raw: string): FactorySuggestions | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  // A bare list of suggestions is accepted too.
  const obj = (Array.isArray(data) ? { suggestions: data } : data) as Record<string, unknown> | null;
  if (!obj || typeof obj !== 'object' || !Array.isArray(obj.suggestions)) return null;
  const suggestions: FactorySuggestion[] = [];
  const ids = new Set<string>();
  for (const [i, item] of obj.suggestions.slice(0, FACTORY_SUGGESTIONS_MAX).entries()) {
    const s = (typeof item === 'string' ? { title: item } : (item ?? {})) as Record<string, unknown>;
    // Titles are one line: they're the key the decisions message shows.
    const title = text(s.title, TITLE_MAX).replace(/\s*\n\s*/g, ' ');
    if (!title) continue;
    let id = text(s.id, 60) || `s${i + 1}`;
    if (ids.has(id)) id = `${id}-${i + 1}`;
    ids.add(id);
    const detail = text(s.detail ?? s.description, DETAIL_MAX);
    const change = text(s.change, NOTE_MAX);
    const without = text(s.without, NOTE_MAX);
    const recommend = recommendedAction(s.recommend);
    // T-99: options read as a question's are (labels, descriptions, the recommended one).
    const options = parseQuestionOptions(s.options);
    const recommended = recommendedOption(s.recommended, options);
    suggestions.push({
      id,
      title,
      ...(detail ? { detail } : {}),
      ...(change ? { change } : {}),
      ...(without ? { without } : {}),
      ...(recommend ? { recommend } : {}),
      ...(options.length ? { options, ...(s.multiSelect === true ? { multiSelect: true } : {}), ...(recommended ? { recommended } : {}) } : {}),
    });
  }
  return suggestions.length ? { suggestions } : null;
}

/** T-99: a decisions block's items (the wrap phase) as suggestions: the question is the title. */
export function suggestionsFromDecisions(decisions: FactoryQuestions): FactorySuggestions {
  const suggestions = decisions.questions.slice(0, FACTORY_SUGGESTIONS_MAX).map((q): FactorySuggestion => {
    const recommend = recommendedAction(q.recommend);
    const full = q.question.replace(/\s*\n\s*/g, ' ');
    const title = (q.header && full.length > TITLE_MAX ? q.header : full).slice(0, TITLE_MAX);
    // A question too long for a title goes in the detail whole, so nothing it says is lost.
    const detail = [title === full ? '' : q.question, q.detail ?? ''].filter(Boolean).join('\n\n');
    return {
      id: q.id,
      title,
      ...(detail ? { detail } : {}),
      ...(q.change ? { change: q.change } : {}),
      ...(q.without ? { without: q.without } : {}),
      ...(recommend ? { recommend } : {}),
      ...(q.options.length ? { options: q.options, ...(q.multiSelect ? { multiSelect: true } : {}), ...(q.recommended ? { recommended: q.recommended } : {}) } : {}),
    };
  });
  return { suggestions };
}

/**
 * The message's suggestions, with where they sit: its first factory-suggestions block, else (T-99) its
 * decisions block in the wrap phase. Null when there's neither (or it's broken).
 */
export function findFactorySuggestions(message: string): { suggestions: FactorySuggestions; start: number; end: number } | null {
  const block = findFencedBlock(message, FACTORY_SUGGESTIONS_LANG);
  const suggestions = block ? parseFactorySuggestionsJson(block.body) : null;
  if (block && suggestions) return { suggestions, start: block.start, end: block.end };
  const decisions = findDecisionsBlock(message);
  if (decisions?.questions.phase !== 'wrap') return null;
  return { suggestions: suggestionsFromDecisions(decisions.questions), start: decisions.start, end: decisions.end };
}

/** One suggestion's decision. `number` is its place in the block (1-based); `note` is why not, or the options picked and a steer. */
export interface SuggestionDecision {
  number: number;
  title: string;
  action: SuggestionAction;
  note: string;
}

const oneLine = (s: string) => s.replace(/\s+/g, ' ').trim();
const cleanTitle = (s: string) => oneLine(s).replace(/\*\*/g, '');
/** How far a note's further lines are indented under its decision (as the factory's). */
const NOTE_INDENT = 3;

/** How a decisions message names the round it decides: the start of the round's comment id. */
export const suggestionsMessageRef = (eventId: string) => eventId.replace(/-/g, '').slice(0, 8).toLowerCase();

/**
 * The decisions as the comment the person posts (and the factory reads): `2. **Title** → Skip: the reason`.
 * `messageRef` names the round when the task has more than one ("**Suggestions · 3f9a1c2e**"); without
 * it, the decisions are for the newest round before them.
 */
export function formatSuggestionDecisions(decisions: SuggestionDecision[], messageRef: string | null = null): string {
  const lines = [messageRef ? `**Suggestions · ${messageRef}**` : '**Suggestions**', ''];
  for (const d of [...decisions].sort((a, b) => a.number - b.number)) {
    // T-99: a note of several lines keeps them, indented under its decision.
    const note = indentFollowingLines(d.note, NOTE_INDENT);
    lines.push(`${d.number}. **${cleanTitle(d.title)}** → ${SUGGESTION_ACTION_LABELS[d.action]}${note ? `: ${note}` : ''}`);
  }
  return lines.join('\n');
}

// The factory's own header may name the run ("**Suggestions from R-12 · 3f9a1c2e**"): read those too.
const HEADER_RE = /^\*\*Suggestions(?: from R-\d+)?(?: · ([0-9a-f]{8}))?\*\*$/;
// The factory's own Ask (a question for the agent, not a decision) isn't read: jflo doesn't offer it.
const LABEL_ACTIONS = new Map<string, SuggestionAction>([
  ...SUGGESTION_ACTIONS.map((a): [string, SuggestionAction] => [SUGGESTION_ACTION_LABELS[a], a]),
  [OLD_NO_LABEL, 'no'],
]);
const LINE_RE = new RegExp(`^(\\d+)\\.\\s+\\*\\*(.*?)\\*\\*\\s+→\\s+(${[...LABEL_ACTIONS.keys()].join('|')})(?::\\s*(.*))?$`);

/** Reads back a decisions message: the round it names (null: none) and each decision; null when the message isn't one. */
export function parseSuggestionDecisions(message: string): { messageRef: string | null; decisions: SuggestionDecision[] } | null {
  const lines = message.replace(/\r\n?/g, '\n').trim().split('\n');
  const header = HEADER_RE.exec(lines[0]?.trim() ?? '');
  if (!header) return null;
  const decisions: SuggestionDecision[] = [];
  for (let i = 1; i < lines.length; ) {
    const m = LINE_RE.exec(lines[i].trim());
    i++;
    if (!m) continue;
    const more = readFollowingLines(lines, i, NOTE_INDENT);
    i = more.next;
    const note = [(m[4] ?? '').trim(), more.text].filter(Boolean).join('\n');
    decisions.push({ number: Number(m[1]), title: m[2], action: LABEL_ACTIONS.get(m[3])!, note });
  }
  return decisions.length ? { messageRef: header[1] ?? null, decisions } : null;
}
